import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Modal} from 'semantic-ui-react';

import {EntitySettings} from '../../generated/graphql';
import {Analytics} from '../../services/analytics';
import {NUM_SECONDS_IN_DAY} from '../../util/date';
import {
  useDeleteArtifactEntityTTL,
  useUpdateArtifactEntityTTL,
} from '../ArtifactTTL/hooks';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../Banner';
import {TeamArtifactTTLChangeSummary} from './TeamArtifactTTLChangeSummary';
import {useUpdateEntitySettings} from './TeamSettingsHook';

type SetEntityArtifactTTLModalProps = {
  entityName: string;
  ttlDurationStringDefault: string | undefined;
  ttlDurationString: string | undefined;
  adminOnlyEditTTLDefault: boolean;
  adminOnlyEditTTL: boolean;
  selectTeamTTLDuration: boolean;
  entitySettings: EntitySettings | null;
  isInvalidTTLInput: boolean;
  saveSettings: () => void;
};

export const SetEntityArtifactTTLModal = ({
  entityName,
  ttlDurationStringDefault,
  ttlDurationString,
  adminOnlyEditTTLDefault,
  adminOnlyEditTTL,
  selectTeamTTLDuration,
  entitySettings,
  isInvalidTTLInput,
  saveSettings,
}: SetEntityArtifactTTLModalProps) => {
  const [showModal, setShowModal] = useState(false);
  ttlDurationString = !selectTeamTTLDuration ? '' : ttlDurationString;
  const changedTTLValue = ttlDurationString !== ttlDurationStringDefault;
  const changedTTLAccess = adminOnlyEditTTL !== adminOnlyEditTTLDefault;
  const removedTTLValue = changedTTLValue && !ttlDurationString;

  const {updateArtifactEntityTTL} = useUpdateArtifactEntityTTL();
  const {deleteArtifactEntityTTL} = useDeleteArtifactEntityTTL();
  const {updateEntitySettings} = useUpdateEntitySettings();

  return (
    entitySettings && (
      <Modal
        open={showModal}
        onOpen={() => setShowModal(true)}
        onClose={() => setShowModal(false)}
        trigger={
          <Button
            variant="primary"
            size="large"
            className="mt-40"
            // Disable if we changed setting or the ttl input is invalid
            disabled={
              (!changedTTLValue && !changedTTLAccess) ||
              (isInvalidTTLInput && !removedTTLValue)
            }>
            Review and save settings
          </Button>
        }
        size="tiny">
        <Modal.Header>Review team's TLL settings</Modal.Header>
        <Modal.Content>
          <Tailwind>
            <div className="font-semibold">Summary of settings</div>
            <div className="outer-changes">
              {changedTTLValue || changedTTLAccess ? (
                <TeamArtifactTTLChangeSummary
                  durationOld={
                    ttlDurationStringDefault
                      ? ttlDurationStringDefault + ' days'
                      : 'None'
                  }
                  durationNew={
                    ttlDurationString ? ttlDurationString + ' days' : 'None'
                  }
                  adminOnlyEditTTLOld={
                    adminOnlyEditTTLDefault
                      ? 'Team admins'
                      : 'Team admins and members'
                  }
                  adminOnlyEditTTLNew={
                    adminOnlyEditTTL ? 'Team admins' : 'Team admins and members'
                  }
                />
              ) : (
                <></>
              )}
            </div>
            {changedTTLValue ? (
              <Banner
                variant={removedTTLValue ? 'info' : 'error'}
                className={removedTTLValue ? 'mt-24' : 'mt-16'}>
                <BannerFlexWrapper>
                  <Icon
                    name={removedTTLValue ? 'info' : 'warning-alt'}
                    className="mr-10 flex-none"
                  />
                  <BannerTextWrapper>
                    <span className="flex">
                      {removedTTLValue ? (
                        <div>
                          Removing the team’s default TTL will retroactively
                          remove the TTL for all artifact versions currently
                          using the default.
                        </div>
                      ) : (
                        <div>
                          {changedTTLValue && ttlDurationStringDefault ? (
                            <div>
                              <div className="inline-block font-semibold">
                                Caution:
                              </div>{' '}
                              Modifying Team-Level TTL with a default{' '}
                              {ttlDurationString}-day duration will trigger the
                              immediate deletion of artifacts older than{' '}
                              {ttlDurationString} days, applying TTL rules
                              retroactively.
                              <br></br>
                              <br></br>
                              Custom TTL settings on individual artifacts remain
                              unaffected by the team's default configuration.
                            </div>
                          ) : (
                            <div>
                              <div className="inline-block font-semibold">
                                Caution:
                              </div>{' '}
                              Enabling Team-Level TTL with a default{' '}
                              {ttlDurationString}-day duration will lead to the
                              immediate deletion of artifacts older than{' '}
                              {ttlDurationString} days, with TTL rules applied
                              retroactively.
                              <br></br>
                              <br></br>
                              Custom TTL settings on individual artifacts will
                              not be affected by the team's default
                              configuration.
                            </div>
                          )}
                        </div>
                      )}
                    </span>
                  </BannerTextWrapper>
                </BannerFlexWrapper>
              </Banner>
            ) : (
              <></>
            )}
            <div className="my-24">
              Are you sure you want to save these changes?
            </div>

            <Button
              style={{
                backgroundColor:
                  changedTTLValue && !removedTTLValue ? 'red' : '',
              }}
              variant="primary"
              size="large"
              onClick={async () => {
                Analytics.track('clicked save team artifact TTL settings', {
                  entityName,
                  ttlDurationDays: ttlDurationString,
                  adminOnlyEditTTL,
                });
                if (removedTTLValue) {
                  await deleteArtifactEntityTTL(entityName);
                } else if (ttlDurationString !== ttlDurationStringDefault) {
                  if (ttlDurationString != null) {
                    const newTTLDurationSeconds =
                      Number(ttlDurationString) * NUM_SECONDS_IN_DAY;
                    await updateArtifactEntityTTL(
                      entityName,
                      newTTLDurationSeconds
                    );
                  } else {
                    toast('Invalid entity TTL');
                  }
                }
                if (adminOnlyEditTTL !== adminOnlyEditTTLDefault) {
                  await updateEntitySettings(entityName, entitySettings, {
                    disableMembersEditArtifactTTL: adminOnlyEditTTL,
                  });
                }
                saveSettings();
                setShowModal(false);
              }}>
              Save settings
            </Button>

            <Button
              variant="ghost"
              size="large"
              onClick={() => setShowModal(false)}
              className="ml-8">
              Cancel
            </Button>
          </Tailwind>
        </Modal.Content>
      </Modal>
    )
  );
};
